/**
 * notePreviewTone – der kurze Orientierungston „so klingt die Note jetzt".
 *
 * Jede tonhöhen-ändernde Bearbeitung (Transponieren, Diatonik, Einfügen/Ersetzen) und jede
 * Notenauswahl in der Eingabe-Vorschau lässt die **resultierende** Tonhöhe hören. Bewusst ein
 * **Oszillator** statt eines SoundFont-Tons:
 * - Er lädt **nichts** nach. Ein SoundFont-Ton bräuchte `playSound()` des `BasicAudioPlayer` und
 *   damit das MB-große Klavier-Sample von der CDN – ein bloßer Notenklick stieße diesen Download an,
 *   und der erste Ton bliebe bis zum Ende des Ladens stumm.
 * - Er funktioniert offline und bevor je auf Play gedrückt wurde.
 *
 * **Der `AudioContext` kommt als Parameter** (`SharedAudioPlayer.previewPitches` reicht seinen
 * durch): Dieses Modul legt selbst keinen an, damit die Invariante „genau EIN AudioContext" hält –
 * festgenagelt in `sourceGuards.spec.ts`. Reines TypeScript, keine Vue-Reaktivität.
 */

/**
 * Vorschau-Ton-Auslöser, wie ihn die Sub-Composables injiziert bekommen: MIDI-Tonhöhen rein, Ton
 * raus. In der Griffschrift-AUSGABE ist es {@link NOOP_PREVIEW_TONE} – dort notiert die Partitur
 * Knopfpositionen, die notierte Tonhöhe wäre schlicht der falsche Klang.
 */
export type NotePreviewTone = (midiKeys: readonly number[]) => void;

/** Stummer Auslöser (Griffschrift-Ausgabe). */
export const NOOP_PREVIEW_TONE: NotePreviewTone = () => {
};

/** Klingende Dauer eines Vorschau-Tons. Lang genug zum Erkennen, kurz genug, dass schnelles
 *  Transponieren (gehaltene ↑-Taste) nicht verschmiert. */
export const PREVIEW_TONE_MS = 450;

/** Deckel gegen eine Oszillator-Wand: eine Takt- oder Part-Auswahl kann dutzende Noten umfassen,
 *  von denen man ohnehin keine mehr einzeln heraushört. */
export const MAX_PREVIEW_TONES = 8;

/** Zeitversatz zwischen zwei Tönen einer Mehrfachauswahl. Die Töne erklingen bewusst
 *  **nacheinander** und nicht als Akkord – nur so hört man jede geänderte Note einzeln heraus. */
export const PREVIEW_TONE_STAGGER_MS = 130;

/** Klingende Dauer je Ton, sobald mehrere nacheinander kommen: kurz genug, dass sich die Kette nicht
 *  wieder zum Akkord staut, lang genug für einen weichen Übergang. */
const CHAINED_TONE_MS = PREVIEW_TONE_STAGGER_MS + 120;

/** Spitzen-Pegel eines einzelnen Tons (bewusst leise – der Ton ist Rückmeldung, nicht Wiedergabe). */
const PEAK_GAIN = 0.22;
const ATTACK_S = 0.012;
const RELEASE_S = 0.12;
/** Ausblendzeit, wenn ein neuer Ton den laufenden ablöst (kein Knacken). */
const CUT_S = 0.03;

/**
 * MIDI-Notennummer → Frequenz bei **A4 = 440 Hz**.
 *
 * Bewusst NICHT `components/harmonika/noteParse.ts::midiToFreq`: das ist die 432-Hz-Stimmung des
 * Harmonika-Simulators und klänge gegenüber der Partitur-Wiedergabe verstimmt.
 */
export function midiToPreviewFrequency(midi: number): number {
    return 440 * 2 ** ((midi - 69) / 12);
}

interface PreviewVoice {
    oscillators: OscillatorNode[];
    gains: GainNode[];
}

let activeVoice: PreviewVoice | null = null;

/** Blendet den laufenden Vorschau-Ton aus (No-op, wenn keiner läuft). */
export function stopPreviewTone(ctx: AudioContext): void {
    const voice = activeVoice;
    if (!voice) return;
    activeVoice = null;
    const now = ctx.currentTime;
    try {
        for (const gain of voice.gains) {
            gain.gain.cancelScheduledValues(now);
            gain.gain.setValueAtTime(gain.gain.value, now);
            gain.gain.linearRampToValueAtTime(0, now + CUT_S);
        }
        // Noch nicht gestartete Töne der Kette spielen gar nicht mehr an (Stopp-Zeit < Startzeit).
        for (const oscillator of voice.oscillators) oscillator.stop(now + CUT_S);
    } catch {
        // Oszillatoren können durch die Browser-Audio-Planung bereits gestoppt sein.
    }
}

/**
 * Spielt die angegebenen MIDI-Tonhöhen als kurze Töne – **nacheinander**, im Abstand
 * {@link PREVIEW_TONE_STAGGER_MS}, nicht gleichzeitig als Akkord: bei einer Mehrfachauswahl soll
 * jede geänderte Note einzeln hörbar sein. Eine Einzelnote klingt entsprechend sofort und in voller
 * Länge.
 *
 * Ein erneuter Aufruf schneidet die laufende Kette ab – beim Halten von ↑ lösen sich die Töne
 * sauber ab, statt sich zu überlagern. Leere Liste → No-op.
 */
export function playPreviewTone(
    ctx: AudioContext, midiKeys: readonly number[], durationMs: number = PREVIEW_TONE_MS,
): void {
    const keys = midiKeys.slice(0, MAX_PREVIEW_TONES);
    if (!keys.length) return;

    stopPreviewTone(ctx);

    const now = ctx.currentTime;
    const stagger = PREVIEW_TONE_STAGGER_MS / 1000;
    const toneLength = (keys.length > 1 ? CHAINED_TONE_MS : durationMs) / 1000;

    const oscillators: OscillatorNode[] = [];
    const gains: GainNode[] = [];

    keys.forEach((midi, index) => {
        const start = now + index * stagger;
        const end = start + toneLength;

        const gain = ctx.createGain();
        gain.gain.value = 0;
        gain.connect(ctx.destination);
        gain.gain.setValueAtTime(0, start);
        gain.gain.linearRampToValueAtTime(PEAK_GAIN, start + ATTACK_S);
        gain.gain.setValueAtTime(PEAK_GAIN, Math.max(start + ATTACK_S, end - RELEASE_S));
        gain.gain.linearRampToValueAtTime(0, end);

        const oscillator = ctx.createOscillator();
        oscillator.type = 'triangle';
        oscillator.frequency.value = midiToPreviewFrequency(midi);
        oscillator.connect(gain);
        oscillator.start(start);
        oscillator.stop(end);

        oscillators.push(oscillator);
        gains.push(gain);
    });

    const voice: PreviewVoice = {oscillators, gains};
    activeVoice = voice;

    const last = oscillators[oscillators.length - 1];
    if (last) {
        last.onended = () => {
            if (activeVoice === voice) activeVoice = null;
            for (const gain of gains) gain.disconnect();
        };
    }
}

export function resetNotePreviewToneForTests(): void {
    activeVoice = null;
}
